/**
 * LarkConnectModal — credential entry form for Lark self-built apps.
 *
 * Shared between IntegrationsPage and MarketplaceDetailModal (see
 * GitlabConnectModal docs for the rationale).
 *
 * Lark connection takes four fields:
 *   - App ID + App Secret  (required, from Developer Console → Credentials)
 *   - Verification Token   (optional, only if you want inbound events)
 *   - Encrypt Key          (optional, only if Lark-side event encryption is on)
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectLark } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

// The Copilot runtime's public ingress host. Each tenant's per-tenant Event
// Request URL is `${COPILOT_RUNTIME_HOST}/lark/events/<appId>`. Overridable via
// REACT_APP_COPILOT_RUNTIME_HOST; default the prod custom domain.
const COPILOT_RUNTIME_HOST =
  process.env.REACT_APP_COPILOT_RUNTIME_HOST || 'https://copilot.zibby.dev';

// Build the exact per-tenant Lark Event Request URL for a given app id. The
// {tenantId} path segment IS the Lark app_id (stable, non-secret) — the runtime
// resolves this tenant's verification/encrypt keys from it before decrypting.
function larkEventUrlFor(appId) {
  const id = String(appId || '').trim();
  return id ? `${COPILOT_RUNTIME_HOST}/lark/events/${encodeURIComponent(id)}` : '';
}

// `overrideProjectId` (optional): connect the app as ONE project's OWN Lark app
// (per-project Copilot override) instead of the account default. Same form,
// same endpoint — the backend keys the row to the project and (on cloud) binds
// the app's events to that project's Copilot.
export default function LarkConnectModal({ isOpen, onClose, onConnected, overrideProjectId }) {
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [encryptKey, setEncryptKey] = useState('');
  // Host/region: 'intl' = International Lark (open.larksuite.com), 'cn' = China
  // Feishu (open.feishu.cn). Default international.
  const [region, setRegion] = useState('intl');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectLark();

  useEffect(() => {
    if (isOpen) {
      setAppId('');
      setAppSecret('');
      setVerificationToken('');
      setEncryptKey('');
      setRegion('intl');
      setCopied(false);
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  // Live per-tenant URL preview — updates as the user types the App ID.
  const eventUrl = larkEventUrlFor(appId);
  const hostValue = region === 'cn' ? 'https://open.feishu.cn' : 'https://open.larksuite.com';

  const copyUrl = async () => {
    if (!eventUrl) return;
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — the URL is still shown for manual copy */ }
  };

  const handleSubmit = () => {
    if (!appId.trim() || !appSecret.trim()) {
      setError('App ID and App Secret are required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      {
        appId: appId.trim(),
        appSecret: appSecret.trim(),
        verificationToken: verificationToken.trim(),
        encryptKey: encryptKey.trim(),
        host: hostValue,
        ...(overrideProjectId ? { overrideProjectId } : {}),
      },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.({ webhookReady: !!verificationToken.trim() });
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Lark. Please check your App ID and App Secret.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
    >
      {/* See GitlabConnectModal for why this stopPropagation matters:
          React events traverse portals, so without it a click in this
          modal bubbles up to the marketplace modal's backdrop and
          closes everything. */}
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>{overrideProjectId ? "Connect this project's Lark app" : 'Connect Lark'}</h2>
        <p className="lark-modal-description">
          Enter the <code>App ID</code> and <code>App Secret</code> from your Lark self-built app (Lark Developer Console → <code>Credentials &amp; Basic Info</code>). Add the <code>Verification Token</code> and optional <code>Encrypt Key</code> below if you want Lark events to trigger Zibby workflows.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label">App ID</label>
          <input
            type="text"
            value={appId}
            placeholder="cli_xxxxxxxxxxxxxxxx"
            onChange={(e) => { setAppId(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">App Secret</label>
          <input
            type="password"
            value={appSecret}
            placeholder="••••••••••••••••••••••••••••••••"
            onChange={(e) => { setAppSecret(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">Host / Region</label>
          <select
            value={region}
            onChange={(e) => { setRegion(e.target.value); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          >
            <option value="intl">International — Lark (open.larksuite.com)</option>
            <option value="cn">China — Feishu / 飞书 (open.feishu.cn)</option>
          </select>
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">
            Verification Token{' '}
            <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional — required for inbound events)</span>
          </label>
          <input
            type="password"
            value={verificationToken}
            placeholder="from Event Subscriptions → Verification Token"
            onChange={(e) => { setVerificationToken(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label">
            Encrypt Key{' '}
            <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional — only if enabled in Lark)</span>
          </label>
          <input
            type="password"
            value={encryptKey}
            placeholder="from Event Subscriptions → Encrypt Key"
            onChange={(e) => { setEncryptKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
          />
        </div>

        {/* Per-tenant Event Request URL — the exact URL the user pastes into the
            Lark dev console. Shown live as they type the App ID so they can copy
            it before/while connecting. */}
        <div className="lark-form-group">
          <label className="lark-form-label">Your Event Request URL</label>
          <p className="lark-modal-description" style={{ marginTop: 0, marginBottom: 6 }}>
            Paste this into <code>事件订阅 / Event Subscriptions → Request URL</code> in the Lark Developer Console.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              readOnly
              value={eventUrl || 'Enter your App ID above to generate this URL'}
              className="lark-form-input"
              style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
              onFocus={(e) => e.target.select()}
            />
            <IonButton
              size="small"
              fill="outline"
              disabled={!eventUrl}
              onClick={copyUrl}
            >
              {copied ? 'Copied' : 'Copy'}
            </IonButton>
          </div>
        </div>

        {error && <p className="lark-modal-error">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !appId.trim() || !appSecret.trim()}
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
